import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, PhoneCall } from "lucide-react";
import { api } from "../../lib/api";
import { CALL_OUTCOMES, OUTCOME_BY_ID, callProblem, proposedNextCall, todayRo } from "../../lib/leadCalls";
import DatePickerField from "./DatePickerField";

/**
 * Record one call to a lead in a few clicks: what came of it, a note, and
 * when to ring again. Used in the lead drawer and in the list's popover.
 *
 * "Nu e interesat" then offers to mark the lead lost — offered, never done
 * silently: the status is the caller's judgement.
 */
export default function LeadCallPanel({ lead, onLogged, compact = false }) {
  const [outcome, setOutcome] = useState("");
  const [note, setNote] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [saving, setSaving] = useState(false);

  const pick = (id) => {
    setOutcome(id);
    setNextDate(proposedNextCall(id) || (OUTCOME_BY_ID[id]?.date === "none" ? "" : nextDate));
  };

  const today = todayRo();
  const problem = callProblem(outcome, nextDate, today);
  const dateMode = OUTCOME_BY_ID[outcome]?.date;

  const save = async () => {
    if (problem) { toast.error(problem); return; }
    setSaving(true);
    try {
      const r = await api.post(`/admin/leads/${lead.id}/calls`, {
        outcome, note, next_call_date: dateMode === "none" ? null : (nextDate || null),
      });
      let updated = r.data;
      toast.success("Apel înregistrat.");
      if (outcome === "not_interested" && lead.status !== "pierdut"
          && window.confirm("Marchezi lead-ul ca „Pierdut”?")) {
        const s = await api.patch(`/admin/leads/${lead.id}`, { status: "pierdut" });
        updated = { ...updated, ...s.data };
      }
      setOutcome(""); setNote(""); setNextDate("");
      onLogged?.(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Apelul nu a putut fi înregistrat.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2.5" data-testid="lead-call-panel">
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Rezultatul apelului">
        {CALL_OUTCOMES.map((o) => {
          const on = outcome === o.id;
          return (
            <button key={o.id} type="button" role="radio" aria-checked={on} onClick={() => pick(o.id)}
              className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${
                on ? "border-[#965EA7] bg-[#965EA7] text-white" : "border-[#E5E7EB] bg-white text-[#373A45] hover:border-[#965EA7]/60"}`}
              data-testid={`lead-call-outcome-${o.id}`}>
              {o.label}
            </button>
          );
        })}
      </div>

      {outcome && dateMode !== "none" && (
        <label className="block text-xs text-[#6B7280]">
          Următorul apel{dateMode === "required" ? " *" : dateMode === "optional" ? " (opțional)" : ""}
          <div className={`mt-1 ${compact ? "" : "max-w-[220px]"}`}>
            <DatePickerField size="sm" value={nextDate} onChange={setNextDate} minDate={today}
              ariaLabel="Data următorului apel" testid="lead-call-next" />
          </div>
        </label>
      )}

      {outcome && (
        <textarea rows={compact ? 2 : 3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Notă (opțional) — ce s-a discutat"
          className="w-full resize-none rounded-lg border border-[#E5E7EB] px-2.5 py-1.5 text-sm text-[#373A45] focus:border-[#965EA7] focus:outline-none"
          data-testid="lead-call-note" />
      )}

      {outcome && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-[#9b1f2c]">{problem}</span>
          <button type="button" onClick={save} disabled={saving || Boolean(problem)}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-[#7A4986] disabled:opacity-40"
            data-testid="lead-call-save">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <PhoneCall size={13} />} Salvează apelul
          </button>
        </div>
      )}
    </div>
  );
}
